/**
 * PsicoSalud — Estructura común de las vistas autenticadas.
 */

import { useCallback, useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import GuideModal, { guiaVista, marcarGuiaVista } from '@/components/GuideModal';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { useAuth } from '@/context/AuthContext';
import { ToastProvider } from '@/context/ToastContext';

export default function Layout() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [guiaAbierta, setGuiaAbierta] = useState(false);
  const [primeraVez, setPrimeraVez] = useState(false);

  // La guía se ofrece sola una vez y solo al caer al panel, que es la primera
  // página después de iniciar sesión. En el resto de las vistas no aparece
  // sola: si alguien ya la cerró, volvérsela a mostrar cada vez que entra a
  // otra pantalla sería una molestia.
  useEffect(() => {
    if (location.pathname !== '/') return;
    if (guiaVista()) return;
    setPrimeraVez(true);
    setGuiaAbierta(true);
  }, [location.pathname]);

  const cerrarGuia = useCallback(() => {
    setGuiaAbierta(false);
    // Se marca como vista al cerrarla, no al abrirla: si alguien la cierra
    // sin leer, no por eso se la vamos a imponer en cada visita.
    if (primeraVez) {
      marcarGuiaVista();
      setPrimeraVez(false);
    }
  }, [primeraVez]);

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <ToastProvider>
      <div className="app-shell">
      <aside className="sidebar">
        <div className="brand">
          {t('app.name')}
          <small>{t('app.tagline')}</small>
        </div>

        <nav>
          <NavLink to="/" end>
            {t('nav.dashboard')}
          </NavLink>
          <NavLink to="/agenda" data-cy="nav-agenda">
            {t('nav.agenda')}
          </NavLink>
          <NavLink to="/pacientes" data-cy="nav-patients">
            {t('nav.patients')}
          </NavLink>
          {/* La administración de cuentas es competencia exclusiva del perfil
              administrativo, y es lo que le da una vista propia en lugar de
              una versión degradada de la del psicólogo (tarea 12). */}
          {user?.role === 'ADMIN' && (
            <NavLink to="/usuarios" data-cy="nav-users">
              {t('nav.users')}
            </NavLink>
          )}
        </nav>

        <div className="sidebar-footer">
          <div>
            <div style={{ fontWeight: 600 }}>{user?.full_name}</div>
            <div className="t-pequeno" style={{ opacity: 0.75 }}>
              {user?.specialization ?? user?.email}
            </div>
            {/* El perfil determina qué se puede ver: mostrarlo evita que
                alguien atribuya a una falla lo que es una restricción. */}
            {user?.role && (
              <div className="t-pequeno" style={{ opacity: 0.6 }} data-cy="current-role">
                {t(`users.roles.${user.role}`)}
              </div>
            )}
          </div>
          <button className="secondary" onClick={handleLogout} data-cy="logout-button">
            {t('nav.logout')}
          </button>
        </div>
      </aside>

      <div className="main-area">
        <header className="topbar">
          <span className="t-suave t-pequeno">
            {new Date().toLocaleDateString('es-PY', {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })}
          </span>
          <div className="topbar-acciones">
            <button
              className="secondary"
              onClick={() => {
                setPrimeraVez(false);
                setGuiaAbierta(true);
              }}
              title={t('guide.title')}
              data-cy="open-guide"
            >
              ?
            </button>
            <LanguageSwitcher />
          </div>
        </header>

        <main className="content">
          <Outlet />
        </main>
      </div>

      <GuideModal abierto={guiaAbierta} onClose={cerrarGuia} primeraVez={primeraVez} />
      </div>
    </ToastProvider>
  );
}
